import { getLocale } from './LocaleSupport';
import { removeNonPrintableCharacters } from '../utilities/stringUtilities';

/**
 * Formatting of numbers and hours.
 */

/**
 * Call this function to convert number to string with specific amount of decimal places.
 *
 * @param {number} value
 * @param {number=2} numberDecimals
 * @return Returns number as string with decimal places specified by passed value.
 *
 * ```typescript
 * const value: number = 5;
 * const formattedNumber: string = formatNumber(value);
 * const formattedNumberWithFiveDecimalPlaces: string = formatNumber(value, 5);
 * ```
 */
export const formatNumber = (value: number, numberDecimals = 2): string => {
   return removeNonPrintableCharacters(new Intl.NumberFormat(getLocale(), {
      minimumFractionDigits: numberDecimals,
      maximumFractionDigits: numberDecimals
   }).format(value));
};

/**
 * Call this function to convert number to an integer string.
 *
 * @param {number} value
 * @return Returns number as string properly formatted as an integer.
 *
 * ```typescript
 * const value: number = 5;
 * const formattedNumber: string = formatInteger(value);
 * ```
 */
export const formatInteger = (value: number): string => {
   return removeNonPrintableCharacters(new Intl.NumberFormat(getLocale(), {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
   }).format(value));
};

/**
 * Call this function to convert hours number to string with 2 decimal places.
 *
 * @param {number} hours
 * @return Returns hours number as string with 2 decimal places.
 *
 * ```typescript
 * const hours: number = 5;
 * const formattedHours: string = formatHours(value);
 * ```
 */
export const formatHours = (hours: number) => {
   return removeNonPrintableCharacters(new Intl.NumberFormat(getLocale(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
   }).format(hours));
};

/**
 * Call this function to return the thousands separator symbol for the current locale
 *
 * @param {}
 * @return The thousands separator symbol
 *
 * ```
 */
export const getThousandsSeparator = () => {
   return (10000).toLocaleString(getLocale()).replace(/[0-9]/g, '');
};

/**
 * Call this function to return the decimal separator symbol for the current locale
 *
 * @param {}
 * @return The decimal separator symbol
 *
 * ```
 */
export const getDecimalSeparator = () => {
   return (1.1).toLocaleString(getLocale()).replace(/[0-9]/g, '');
};
